import React, { ReactNode, useEffect, useState } from 'react';
import useDocumentFiles from '../../../../apps/document/hooks/useDocumentFiles';
import HAppRoutes from '../../routes/helpers/HAppRoutes';
import IAppNotification from '../interfaces/IAppNotification';
import DocumentRoutes from '../../../../apps/document/DocumentRoutes';
import _ from 'lodash';

type IAppNotificationContext = {
	notifications: IAppNotification[] | null;
	addNotification: (notification: IAppNotification) => void;
	resetNotifications: () => void;
};

const AppNotificationContext = React.createContext<IAppNotificationContext>({
	notifications: null,
	addNotification: () => {
		null;
	},
	resetNotifications: () => {
		null;
	},
});

export const useAppNotifications = () => {
	return React.useContext(AppNotificationContext);
};

type AppNotificationProviderProps = {
	children?: ReactNode;
};

const AppNotificationProvider: React.FC<AppNotificationProviderProps> = ({ children }) => {
	const [notifications, setNotifications] = useState<IAppNotification[] | null>(null);
	const addNotification = (notification: IAppNotification) => {
		setNotifications((prevData) => [...(prevData ?? []), notification]);
	};

	const resetNotifications = () => {
		setNotifications(null);
	};

	const deleteNotification = (documentId: number) => {
		setNotifications((prevData) => (prevData ?? []).filter((x) => x.id !== documentId));
	};

	const { documents } = useDocumentFiles();
	useEffect(() => {
		const allNotifications: IAppNotification[] = [];
		if (documents && !notifications) {
			documents
				.filter((x) => x.isNew)
				.forEach((document) => {
					allNotifications.push({
						id: document.id,
						typeTag: 'document',
						title: 'Un nouveau document disponible',
						icon: document.formatTag,
						message: document.name,
						onclick: () => {
							deleteNotification(document.id);
						},
						to: HAppRoutes.getPath(DocumentRoutes.document.tag, { id: document.id.toString() }),
					});
				});
			if (_.differenceWith(allNotifications, notifications ?? [], _.isEqual)) {
				setNotifications(allNotifications);
			}
		}
	}, [documents, notifications]);

	const contextValue = React.useMemo(() => {
		return {
			notifications,
			addNotification,
			resetNotifications,
		};
	}, [notifications]);

	return <AppNotificationContext.Provider value={contextValue}>{children}</AppNotificationContext.Provider>;
};

export default AppNotificationProvider;
